import {
  canClaimSupplierMatch,
  hasSupplierMatchAccount,
  hasSupplierMatchClaimOnFile,
  isSupplierMatchCheckEligible
} from "@/lib/supplier-match-status";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";

type SupplierMatchView = SalonStockerHttpTypes.StoreSupplierMatchStatusView;

/**
 * The suppliers the "save your trade accounts" card lists on a new chat: none
 * when the salon already has a claimed or matched supplier (it has set up
 * trade accounts, so the card has done its job), otherwise every claimable
 * supplier the salon has no claim on file for. Left out:
 *
 * - a supplier with a claim awaiting its confirmation click: tapping Save
 *   would repoint it at the login email and drop the address the salon chose;
 * - a supplier the salon released: that was a deliberate "no", and the
 *   Supplier Match page is where to undo it.
 *
 * A salon whose only claimable supplier is one of those sees no card.
 */
export function claimableSuppliers(
  views: readonly SupplierMatchView[]
): SupplierMatchView[] {
  if (views.some(hasSupplierMatchAccount)) return [];
  return views.filter(
    (view) =>
      canClaimSupplierMatch(view) &&
      !hasSupplierMatchClaimOnFile(view) &&
      view.status !== "released"
  );
}

/**
 * Whether the auto-check could change what the card shows: there is a supplier
 * to list and a lookup supplier the check would look up. Without a possible
 * card the check is not worth the external lookups it makes.
 */
export function autoCheckWorthRunning(
  views: readonly SupplierMatchView[]
): boolean {
  return (
    claimableSuppliers(views).length > 0 &&
    views.some(isSupplierMatchCheckEligible)
  );
}

/** Whether the browser reports no connection. */
export function isOffline(): boolean {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

const DISMISSED_KEY_PREFIX = "ss:trade-account-card-dismissed:";

/** The local calendar day, so "until tomorrow" follows the salon's own clock. */
function dayKey(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Dismissals made in this page load, per customer. The fallback for storage
 * that throws (private mode, embedded webview): without it a dismissal would
 * last only until the card remounts, i.e. until the drawer is next opened.
 */
const dismissedThisLoad = new Map<string, string>();

/**
 * Whether the salon tapped "Not now" today. Client-only and per customer (a
 * different salon on the same browser is not affected). Storage can be
 * unavailable: then the dismissal lasts for this page load only.
 */
export function isTradeAccountCardDismissed(
  customerId: string,
  now: Date = new Date()
): boolean {
  const today = dayKey(now);
  if (dismissedThisLoad.get(customerId) === today) return true;
  try {
    return (
      window.localStorage.getItem(`${DISMISSED_KEY_PREFIX}${customerId}`) ===
      today
    );
  } catch {
    return false;
  }
}

/** Hide the card until the next calendar day. Never throws. */
export function dismissTradeAccountCard(
  customerId: string,
  now: Date = new Date()
): void {
  const today = dayKey(now);
  dismissedThisLoad.set(customerId, today);
  try {
    window.localStorage.setItem(`${DISMISSED_KEY_PREFIX}${customerId}`, today);
  } catch {
    // Quota or unavailable storage: `dismissedThisLoad` still holds it.
  }
}
